import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building,
  Search,
  Filter,
  RotateCcw,
  Boxes,
  ShoppingCart,
  Package,
  BadgeDollarSign,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  User,
  ArrowRight,
  X,
  Eye,
  Calendar,
  Layers,
  FileText
} from "lucide-react";
import { adminService } from "../api/adminService";
import { useNotification } from "../contexts/NotificationContext";

export default function TenantAuditTrail() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const { showError } = useNotification();

  const [tenant, setTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTenant, setLoadingTenant] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedModule, setSelectedModule] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit] = useState(20);

  // Drawer Detail
  const [selectedLog, setSelectedLog] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load Tenant info and Tenant Users
  useEffect(() => {
    if (!tenantId) return;

    const fetchTenantInfo = async () => {
      try {
        setLoadingTenant(true);
        const [tenantRes, usersRes] = await Promise.all([
          adminService.tenants.getById(tenantId),
          adminService.tenants.getTenantUsers(tenantId).catch(() => ({ data: [] }))
        ]);

        const tData = tenantRes?.data?.data || tenantRes?.data || tenantRes;
        setTenant(tData);

        const uData = usersRes?.data?.data || usersRes?.data || [];
        setTenantUsers(Array.isArray(uData) ? uData : []);
      } catch (err) {
        console.error("Gagal memuat informasi tenant:", err);
        showError("Gagal memuat profil tenant");
      } finally {
        setLoadingTenant(false);
      }
    };

    fetchTenantInfo();
  }, [tenantId, showError]);

  // Calculate actual start and end date based on dateRange
  const getDateParams = useCallback(() => {
    const now = new Date();
    if (dateRange === "today") {
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      return { start_date: `${y}-${m}-${d}`, end_date: `${y}-${m}-${d}` };
    } else if (dateRange === "7days") {
      const past = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return {
        start_date: past.toISOString().split("T")[0],
        end_date: now.toISOString().split("T")[0]
      };
    } else if (dateRange === "30days") {
      const past = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return {
        start_date: past.toISOString().split("T")[0],
        end_date: now.toISOString().split("T")[0]
      };
    } else if (dateRange === "custom") {
      return {
        start_date: startDate || undefined,
        end_date: endDate || undefined
      };
    }
    return {};
  }, [dateRange, startDate, endDate]);

  // Fetch Audit Logs
  const fetchAuditLogs = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const dateParams = getDateParams();
      const params = {
        page: currentPage,
        limit,
        search: debouncedSearch || undefined,
        module: selectedModule !== "all" ? selectedModule : undefined,
        user_id: selectedUser !== "all" ? selectedUser : undefined,
        ...dateParams
      };

      const res = await adminService.tenants.getAuditTrail(tenantId, params);
      let items = [];
      let pagination = {};

      if (Array.isArray(res)) {
        items = res;
      } else if (Array.isArray(res?.data?.data)) {
        items = res.data.data;
        pagination = res.data.pagination || res.data.meta || res.pagination || {};
      } else if (Array.isArray(res?.data)) {
        items = res.data;
        pagination = res.pagination || res.meta || {};
      } else if (res?.data) {
        items = res.data.items || res.data.records || [];
        pagination = res.data.pagination || res.data.meta || {};
      }

      setLogs(items);
      setTotalPages(pagination.total_pages || 1);
      setTotalRecords(pagination.total ?? items.length ?? 0);
    } catch (err) {
      console.error("Gagal memuat audit trail:", err);
      showError(err?.message || "Gagal memuat catatan audit trail");
      setLogs([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [tenantId, currentPage, limit, debouncedSearch, selectedModule, selectedUser, getDateParams, showError]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handleResetFilters = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setSelectedModule("all");
    setSelectedUser("all");
    setDateRange("all");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const handleOpenDetail = (logItem) => {
    setSelectedLog(logItem);
    setIsDrawerOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDrawerOpen(false);
    setSelectedLog(null);
  };

  // Format datetime
  const formatDateTime = (isoString) => {
    if (!isoString) return { dateStr: "-", timeStr: "" };
    try {
      const dt = new Date(isoString);
      if (isNaN(dt.getTime())) return { dateStr: String(isoString), timeStr: "" };
      const dateStr = dt.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
      const timeStr = dt.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit"
      }).replace(".", ".") + " WIB";
      return { dateStr, timeStr };
    } catch {
      return { dateStr: String(isoString), timeStr: "" };
    }
  };

  // Render module badge
  const renderModuleBadge = (moduleName) => {
    switch (moduleName) {
      case "Penjualan":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
            <ShoppingCart className="w-3.5 h-3.5 text-blue-600" />
            Penjualan
          </span>
        );
      case "Inventori":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <Boxes className="w-3.5 h-3.5 text-amber-600" />
            Inventori
          </span>
        );
      case "Produk":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
            <Package className="w-3.5 h-3.5 text-purple-600" />
            Produk
          </span>
        );
      case "Kasir":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <BadgeDollarSign className="w-3.5 h-3.5 text-emerald-600" />
            Kasir
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
            <Layers className="w-3.5 h-3.5 text-gray-500" />
            {moduleName || "Sistem"}
          </span>
        );
    }
  };

  // Render status badge
  const renderStatusBadge = (statusStr) => {
    const s = String(statusStr || "").toUpperCase();
    if (s === "BERHASIL" || s === "DONE" || s === "PAID" || s === "APPROVED" || s === "CLOSED") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
          {s === "CLOSED" ? "DITUTUP" : "Berhasil"}
        </span>
      );
    }
    if (s === "CANCELLED" || s === "REJECTED" || s === "VOID") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200">
          <XCircle className="w-3 h-3 text-rose-600" />
          {s === "CANCELLED" ? "Dibatalkan" : s}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
        <AlertCircle className="w-3 h-3 text-amber-600" />
        {s || "Diproses"}
      </span>
    );
  };

  // Get User Avatar Initials
  const getInitials = (name) => {
    if (!name) return "SA";
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="p-6 space-y-6 w-full">
      {/* 1. Breadcrumb Navigasi */}
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <button
          onClick={() => navigate("/admin/tenants")}
          className="hover:text-teal-600 font-medium transition-colors"
        >
          Tenant Management
        </button>
        <span>/</span>
        <span className="text-gray-700 font-medium">{tenant?.name || "Memuat..."}</span>
        <span>/</span>
        <span className="font-semibold text-teal-600">Audit Trail</span>
      </div>

      {/* 2. Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Audit Trail Tenant</h1>
          <p className="text-sm text-gray-500 mt-1">
            Pantau seluruh perubahan stok, status nota penjualan, penyesuaian inventori, dan sesi kasir langsung dari database.
          </p>
        </div>
        <button
          onClick={() => navigate("/admin/tenants")}
          className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 hover:border-teal-500 hover:text-teal-600 transition-all shadow-sm self-start sm:self-auto"
        >
          <ArrowLeft className="w-4 h-4" />
          Kembali ke Tenant
        </button>
      </div>

      {/* 3. Tenant Context Header Card */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-5">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center text-teal-600 font-bold text-lg flex-shrink-0">
            <Building className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="text-lg font-bold text-gray-900 truncate">{tenant?.name || "Nama Tenant"}</h2>
              <span
                className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                  tenant?.status === "ACTIVE"
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-rose-100 text-rose-800"
                }`}
              >
                {tenant?.status || "ACTIVE"}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1 truncate">
              Slug: <span className="font-mono text-gray-700 font-medium">{tenant?.slug || "-"}</span>
              {tenant?.settings?.address && <span> • {tenant.settings.address}</span>}
              {tenant?.settings?.phone && <span> • Telp {tenant.settings.phone}</span>}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-6 border-t md:border-t-0 pt-3 md:pt-0 flex-shrink-0">
          <div>
            <div className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Paket Langganan</div>
            <div className="text-sm font-bold text-gray-800 uppercase mt-0.5">{tenant?.subscription_plan || "FREE"}</div>
          </div>
          <div className="border-l border-gray-200 pl-6">
            <div className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Total Riwayat</div>
            <div className="text-sm font-bold text-teal-600 mt-0.5">{totalRecords.toLocaleString("id-ID")} Aktivitas</div>
          </div>
        </div>
      </div>

      {/* 4. Filter Panel */}
      <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
          {/* Pencarian */}
          <div className="lg:col-span-4">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Cari aktivitas, produk, atau referensi
            </label>
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Contoh: TRX-20260911-006, SILENSIA..."
                className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-gray-50/50"
              />
            </div>
          </div>

          {/* Filter Modul */}
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Modul</label>
            <select
              value={selectedModule}
              onChange={(e) => {
                setSelectedModule(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
            >
              <option value="all">Semua Modul</option>
              <option value="Penjualan">Penjualan</option>
              <option value="Inventori">Inventori / Mutasi Stok</option>
              <option value="Produk">Produk</option>
              <option value="Kasir">Kasir / Shift</option>
            </select>
          </div>

          {/* Filter Pengguna */}
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Pengguna</label>
            <select
              value={selectedUser}
              onChange={(e) => {
                setSelectedUser(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
            >
              <option value="all">Semua Pengguna</option>
              {tenantUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name || u.email}
                </option>
              ))}
            </select>
          </div>

          {/* Filter Periode Waktu */}
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Periode Waktu</label>
            <select
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
            >
              <option value="all">Semua Waktu</option>
              <option value="today">Hari Ini</option>
              <option value="7days">7 Hari Terakhir</option>
              <option value="30days">30 Hari Terakhir</option>
              <option value="custom">Kustom Tanggal</option>
            </select>
          </div>

          {/* Tombol Reset */}
          <div className="lg:col-span-2 flex gap-2">
            <button
              onClick={handleResetFilters}
              type="button"
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-700 bg-white rounded-xl text-xs font-medium hover:bg-gray-50 hover:text-teal-600 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset Filter
            </button>
          </div>
        </div>

        {/* Input kustom rentang tanggal jika dipilih */}
        {dateRange === "custom" && (
          <div className="pt-2 border-t border-gray-100 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 font-medium">Dari:</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 font-medium">Sampai:</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* 5. Tabel Riwayat Audit Trail */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-sm text-gray-900">Riwayat Aktivitas</h3>
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
              {totalRecords.toLocaleString("id-ID")}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            Halaman <span className="font-semibold text-gray-700">{currentPage}</span> dari{" "}
            <span className="font-semibold text-gray-700">{totalPages}</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                <th className="py-3 px-4 whitespace-nowrap">Waktu</th>
                <th className="py-3 px-4 whitespace-nowrap">Pengguna</th>
                <th className="py-3 px-4 whitespace-nowrap">Modul</th>
                <th className="py-3 px-4 min-w-[220px]">Aktivitas</th>
                <th className="py-3 px-4 whitespace-nowrap">Referensi</th>
                <th className="py-3 px-4 whitespace-nowrap">Status</th>
                <th className="py-3 px-4 text-right whitespace-nowrap">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-gray-400">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                      <span>Memuat catatan audit trail...</span>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="7" className="py-12 text-center text-gray-400">
                    Tidak ada aktivitas atau mutasi yang sesuai dengan filter pencarian.
                  </td>
                </tr>
              ) : (
                logs.map((item) => {
                  const { dateStr, timeStr } = formatDateTime(item.event_time);
                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-teal-50/40 transition-colors group cursor-pointer"
                      onClick={() => handleOpenDetail(item)}
                    >
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className="font-medium text-gray-800">{dateStr}</div>
                        <div className="text-[11px] text-gray-400 font-mono mt-0.5">{timeStr}</div>
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-[11px] flex-shrink-0">
                            {getInitials(item.user_name)}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-800">{item.user_name || "Sistem"}</div>
                            <div className="text-[10px] text-gray-400">{item.user_role || "User"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        {renderModuleBadge(item.module)}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="font-bold text-gray-900">{item.activity_title}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">
                          {item.activity_detail}
                        </div>
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap font-mono text-[11px] text-gray-700">
                        {item.reference_no && item.reference_no !== "-" ? (
                          <span className="px-2 py-0.5 bg-gray-100 rounded-md font-semibold text-gray-700">
                            {item.reference_no}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        {renderStatusBadge(item.status)}
                      </td>
                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDetail(item);
                          }}
                          className="px-2.5 py-1 text-xs font-semibold text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg transition-colors inline-flex items-center gap-1"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Detail
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Paginasi */}
        {!loading && logs.length > 0 && (
          <div className="p-4 border-t border-gray-200 flex items-center justify-between flex-wrap gap-3 bg-gray-50/50">
            <div className="text-xs text-gray-500">
              Menampilkan{" "}
              <span className="font-semibold text-gray-700">
                {(currentPage - 1) * limit + 1} - {Math.min(currentPage * limit, totalRecords)}
              </span>{" "}
              dari <span className="font-semibold text-gray-700">{totalRecords}</span> aktivitas
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Sebelumnya
              </button>
              <div className="px-3 py-1.5 text-xs font-semibold text-gray-700">
                {currentPage} / {totalPages}
              </div>
              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Selanjutnya
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 6. Slide-Over Detail Drawer */}
      {isDrawerOpen && selectedLog && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
            onClick={handleCloseDetail}
          />

          <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
            <div className="w-screen max-w-md bg-white shadow-2xl border-l border-gray-200 flex flex-col transform transition-transform duration-300 ease-in-out">
              {/* Drawer Header */}
              <div className="p-5 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <div>
                  <div className="text-[10px] font-bold tracking-wider text-teal-600 uppercase">
                    Rincian Aktivitas
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mt-0.5">
                    {selectedLog.activity_title}
                  </h3>
                </div>
                <button
                  onClick={handleCloseDetail}
                  className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Drawer Content */}
              <div className="flex-1 overflow-y-auto p-5 space-y-5 text-xs">
                {/* Status & Modul Baris Atas */}
                <div className="flex items-center justify-between">
                  <div>{renderModuleBadge(selectedLog.module)}</div>
                  <div>{renderStatusBadge(selectedLog.status)}</div>
                </div>

                {/* Grid Rincian Meta */}
                <div className="bg-gray-50/70 border border-gray-200 rounded-xl p-3.5 space-y-2.5">
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-gray-500 font-medium">Waktu:</span>
                    <span className="col-span-2 text-gray-900 font-semibold">
                      {formatDateTime(selectedLog.event_time).dateStr},{" "}
                      {formatDateTime(selectedLog.event_time).timeStr}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-gray-500 font-medium">Pengguna:</span>
                    <span className="col-span-2 text-gray-900 font-semibold">
                      {selectedLog.user_name} ({selectedLog.user_role || "User"})
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-gray-500 font-medium">Referensi:</span>
                    <span className="col-span-2 font-mono font-bold text-teal-700">
                      {selectedLog.reference_no || "-"}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <span className="text-gray-500 font-medium">Keterangan:</span>
                    <span className="col-span-2 text-gray-800">
                      {selectedLog.activity_detail || "-"}
                    </span>
                  </div>
                </div>

                {/* Kotak Perubahan Nilai (Sebelum vs Sesudah) jika ada */}
                {selectedLog.before_value && selectedLog.before_value !== "-" && (
                  <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm space-y-2.5">
                    <div className="font-bold text-gray-800 text-xs flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-teal-600" />
                      {selectedLog.change_title || "Perubahan Nilai"}
                    </div>
                    <div className="grid grid-cols-7 items-center gap-2 bg-gray-50 p-3 rounded-lg border border-gray-100">
                      <div className="col-span-3 text-center">
                        <div className="text-[10px] text-gray-400 font-medium uppercase">Sebelum</div>
                        <div className="text-sm font-bold text-gray-800 mt-1">
                          {selectedLog.before_value}
                        </div>
                      </div>
                      <div className="col-span-1 flex justify-center text-teal-600">
                        <ArrowRight className="w-4 h-4" />
                      </div>
                      <div className="col-span-3 text-center">
                        <div className="text-[10px] text-gray-400 font-medium uppercase">Sesudah</div>
                        <div className="text-sm font-bold text-teal-700 mt-1">
                          {selectedLog.after_value}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Data Ekstra Tambahan */}
                {selectedLog.extra_data && Object.keys(selectedLog.extra_data).length > 0 && (
                  <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm space-y-2">
                    <div className="font-bold text-gray-800 text-xs">Informasi Tambahan</div>
                    <div className="space-y-1.5 text-[11px]">
                      {Object.entries(selectedLog.extra_data).map(([key, val]) => {
                        if (val === null || val === undefined || val === "") return null;
                        const label = key
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase());
                        let displayVal = typeof val === "object" ? JSON.stringify(val) : String(val);
                        if (key.includes("total") || key.includes("cash") || key.includes("price")) {
                          const num = Number(val);
                          if (!isNaN(num)) {
                            displayVal = `Rp ${num.toLocaleString("id-ID")}`;
                          }
                        }
                        return (
                          <div key={key} className="flex justify-between py-1 border-b border-gray-100 last:border-0">
                            <span className="text-gray-500">{label}:</span>
                            <span className="font-semibold text-gray-800 text-right">{displayVal}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Drawer Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                <button
                  onClick={handleCloseDetail}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl text-xs font-semibold hover:bg-gray-300 transition-colors"
                >
                  Tutup Rincian
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
